import { Search, Download } from "lucide-react";
import { DIVISION_COLOR } from "@/lib/constants";

export const UNASSIGNED = "Belum ditetapkan";
const NONE = "__none__";

export const divisionLabel = (r) => r.division_name || UNASSIGNED;
export const subdivisionLabel = (r) => r.subdivision_name || UNASSIGNED;
const divKey = (r) => r.division_id || NONE;
const subKey = (r) => r.subdivision_id || NONE;

export function DivisionCell({ row, className = "px-4 py-3", testid }) {
  return (
    <td className={className} data-testid={testid}>
      {row.division_name
        ? <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[row.division_name] || "bg-slate-100"}`}>{row.division_name}</span>
        : <span className="text-[11px] text-slate-400 italic">{UNASSIGNED}</span>}
    </td>
  );
}

export function SubdivisionCell({ row, className = "px-4 py-3", testid }) {
  return (
    <td className={`${className} text-xs ${row.subdivision_name ? "text-slate-600" : "text-slate-400 italic"}`} data-testid={testid}>
      {subdivisionLabel(row)}
    </td>
  );
}

export function OrgLines({ row, testid }) {
  return (
    <span className="inline-flex flex-col text-[10px] text-slate-500 leading-tight" data-testid={testid}>
      <span>Divisi: <span className="text-slate-700">{divisionLabel(row)}</span></span>
      <span>Sub-divisi: <span className="text-slate-700">{subdivisionLabel(row)}</span></span>
    </span>
  );
}

export function buildOrgOptions(rows) {
  const divs = new Map();
  for (const r of rows) {
    const dk = divKey(r);
    if (!divs.has(dk)) divs.set(dk, { id: dk, name: r.division_id ? r.division_name : "Tidak terhubung master", subs: new Map() });
    const sk = subKey(r);
    if (!divs.get(dk).subs.has(sk)) divs.get(dk).subs.set(sk, { id: sk, name: r.subdivision_id ? r.subdivision_name : UNASSIGNED });
  }
  return [...divs.values()].map((d) => ({ ...d, subs: [...d.subs.values()].sort((a, b) => a.name.localeCompare(b.name)) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function matchOrg(r, { divisionId, subdivisionId, q }) {
  if (divisionId && divKey(r) !== divisionId) return false;
  if (subdivisionId && subKey(r) !== subdivisionId) return false;
  const s = (q || "").trim().toLowerCase();
  if (!s) return true;
  return [r.name, r.division_name, r.subdivision_name].some((v) => (v || "").toLowerCase().includes(s));
}

export function OrgFilterBar({ options, filter, setFilter, onExport }) {
  const div = options.find((d) => d.id === filter.divisionId);
  const subs = div ? div.subs : options.flatMap((d) => d.subs.map((s) => ({ ...s, key: `${d.id}|${s.id}`, label: `${s.name} (${d.name})` })));
  const cls = "border border-slate-300 h-9 px-2 text-xs focus:outline-none focus:border-[#0B1D3A]";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="perf-org-filter">
      <div className="relative">
        <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} placeholder="Cari nama, divisi, sub-divisi…"
          data-testid="perf-org-search" className={`${cls} pl-7 w-64`} />
      </div>
      <select value={filter.divisionId} onChange={(e) => setFilter({ ...filter, divisionId: e.target.value, subdivisionId: "" })} data-testid="perf-org-division" className={cls}>
        <option value="">Semua Divisi</option>
        {options.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
      </select>
      <select value={filter.subdivisionId} onChange={(e) => setFilter({ ...filter, subdivisionId: e.target.value })} data-testid="perf-org-subdivision" className={cls}>
        <option value="">Semua Sub-divisi</option>
        {div ? subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)
          : subs.filter((s, i, a) => a.findIndex((x) => x.id === s.id) === i).map((s) => <option key={s.key} value={s.id}>{s.label}</option>)}
      </select>
      {onExport && (
        <button type="button" onClick={onExport} data-testid="perf-export-csv"
          className="border border-slate-300 h-9 px-3 uppercase tracking-wider font-semibold text-xs inline-flex items-center gap-1.5 hover:bg-slate-50 text-slate-600">
          <Download size={13} /> Ekspor CSV
        </button>
      )}
    </div>
  );
}

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export function downloadCsv(filename, header, rows) {
  const body = [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob(["\uFEFF" + body], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
